import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import ROUTES from "shared/routes";
import { isSessionEndedError } from "./auth-actions";
import { AuthError } from "./auth-error";
import { getAuthErrorMessage } from "./auth-error-message";
import { AuthSession, clearAuthSession, loadAuthSession } from "./auth-session";
import { useCountdown } from "./use-countdown";

/**
 * Общее для экранов входа: возврат на ввод номера, когда вход закончен
 * (ошибка сервера или истёк срок сессии).
 */
export const useAuthRestart = ({
  expiresAt,
  watchExpiry,
}: {
  /** Срок текущей сессии. */
  expiresAt?: string;
  /** Следить за сроком (выключается, например, после успешного входа). */
  watchExpiry: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();

  const restart = useCallback(
    (reason?: string) => {
      clearAuthSession();
      navigate(`${ROUTES.LOGIN}?${search}`, {
        replace: true,
        state: reason ? { error: reason } : undefined,
      });
    },
    [navigate, search],
  );

  /**
   * Если ошибка закончила вход — возвращает на ввод номера и отдаёт true.
   * Иначе отдаёт текст ошибки для показа на экране.
   */
  const handleAuthError = useCallback(
    (error: AuthError, session: AuthSession | null): string | true => {
      const message = getAuthErrorMessage(t, error, session);
      if (isSessionEndedError(error, session)) {
        restart(message);
        return true;
      }
      return message;
    },
    [restart, t],
  );

  /**
   * Актуальная сессия из хранилища. Если её уже нет (истекла) —
   * возвращает на ввод номера и отдаёт null.
   */
  const reloadSession = useCallback((): AuthSession | null => {
    const session = loadAuthSession();
    if (!session) {
      restart(t("auth.errors.session_expired"));
    }
    return session;
  }, [restart, t]);

  const secondsLeft = useCountdown(expiresAt);
  const isExpired = watchExpiry && !!expiresAt && secondsLeft === 0;

  useEffect(() => {
    if (isExpired) {
      restart(t("auth.errors.session_expired"));
    }
  }, [isExpired, restart, t]);

  return { restart, handleAuthError, reloadSession, search };
};
